import {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {DataTable, type Column} from '@/components/data-table'
import {TrashGlyph} from '@/components/ui/icons'
import {ListCard, ListEmptyState} from '@/components/section-card'
import {NuevaEvaluacionDialog} from '@/components/bateria/nueva-evaluacion-dialog'
import {EliminarEvaluacionDialog} from '@/components/bateria/eliminar-evaluacion-dialog'
import {BateriaSolicitudesSection} from '@/components/bateria/bateria-solicitudes-section'
import {EVALUACIONES_PAGE_SIZE, useEvaluaciones, type BateriaEvaluacion} from '@/hooks/bateria/use-bateria'
import {EVALUACION_ESTADO_TONE} from '@/lib/bateria'

const dateFormat = new Intl.DateTimeFormat('es-CO', {year: 'numeric', month: 'short', day: 'numeric'})

export function BateriaTab({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<BateriaEvaluacion | null>(null)
  const {data, isLoading} = useEvaluaciones(businessId, page, true)

  const columns: Column<BateriaEvaluacion>[] = [
    {
      key: 'fecha_inicio',
      header: t('admin.bateria.list.date'),
      width: 'w-32',
      cell: (e) => (
        <span className="block font-mono text-xs text-foreground">{dateFormat.format(new Date(e.fecha_inicio))}</span>
      ),
    },
    {
      key: 'psicologo',
      header: t('admin.bateria.list.psicologo'),
      // Sin ancho propio, a 390px esta columna colapsa a cero y su encabezado se parte letra por letra.
      width: 'w-40 sm:w-auto',
      cell: (e) => (
        <div className="min-w-0">
          <span className="block truncate font-medium text-foreground">{e.psicologo_name}</span>
          {e.notas && (
            <span className="block truncate text-xs text-muted-foreground" data-testid={`evaluacion-notas-${e.id}`}>
              {e.notas.split('\n')[0]}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'estado',
      header: t('admin.bateria.list.estado'),
      width: 'w-28',
      // Mismo tono que el sello de estado del detalle: cerrada y entregada son momentos distintos.
      cell: (e) => <Tag tone={EVALUACION_ESTADO_TONE[e.estado]}>{t(`admin.bateria.evaluacionEstado.${e.estado}`)}</Tag>,
    },
    {
      key: 'acciones',
      header: '',
      width: 'w-12',
      className: 'text-right',
      cell: (e) =>
        e.estado !== 'entregada' ? (
          <button
            type="button"
            aria-label={t('admin.bateria.list.eliminar')}
            data-testid={`eliminar-evaluacion-${e.id}`}
            onClick={(ev) => {
              ev.stopPropagation()
              setDeleting(e)
            }}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-destructive">
            <TrashGlyph />
          </button>
        ) : null,
    },
  ]

  return (
    <div className="space-y-4" data-testid="bateria-tab">
      <BateriaSolicitudesSection />

      {isLoading && !data ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <ListCard
          title={t('admin.bateria.listTitle')}
          actions={
            <Button size="sm" data-testid="nueva-evaluacion" onClick={() => setCreating(true)}>
              {t('admin.bateria.nueva')}
            </Button>
          }
          footer={
            !!data?.total && (
              <PaginationBar page={page} pageSize={EVALUACIONES_PAGE_SIZE} total={data.total} onPageChange={setPage} />
            )
          }>
          <DataTable
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(e) => e.id}
            onRowClick={(e) => navigate(`/admin/bateria/empresas/${businessId}/evaluaciones/${e.id}`)}
            emptyState={<ListEmptyState page title={t('admin.bateria.emptyTitle')} />}
          />
        </ListCard>
      )}

      <NuevaEvaluacionDialog
        businessId={businessId}
        open={creating}
        onOpenChange={setCreating}
        onCreated={(id) => navigate(`/admin/bateria/empresas/${businessId}/evaluaciones/${id}`)}
      />

      {deleting && (
        <EliminarEvaluacionDialog
          businessId={businessId}
          evaluacionId={deleting.id}
          fechaInicio={deleting.fecha_inicio}
          psicologoName={deleting.psicologo_name}
          estado={deleting.estado}
          personasConPaquete={deleting.aplicados}
          open={!!deleting}
          onOpenChange={(open) => !open && setDeleting(null)}
          onDeleted={() => setDeleting(null)}
        />
      )}
    </div>
  )
}
